// src/components/resources/calculators/video-dropoff/utils/platform-breakpoints.ts

/**
 * @file Platform Breakpoints Utilities
 * @description Functions for generating platform-specific breakpoints
 */

import type { Platform, Breakpoint } from '~/components/resources/calculators/video-dropoff/types'
import { PLATFORM_CONSTANTS } from '~/components/resources/calculators/video-dropoff/types/constants'

/**
 * Generates a default set of Breakpoint objects based on the selected platform,
 * video length, and total views. Uses typical retention patterns for each platform.
 *
 * @param {Platform} platform - The selected video platform ('facebook', 'tiktok', 'youtube', 'vimeo', 'custom').
 * @param {number} videoLength - The total length of the video in seconds.
 * @param {number} totalViews - The total number of initial video views.
 * @returns {Breakpoint[]} An array of default Breakpoint objects for the specified platform.
 */
export const generatePlatformBreakpoints = (platform: Platform, videoLength: number, totalViews: number): Breakpoint[] => {
  // Realistic drop-off patterns based on platform
  // Initial steep drop-off (80-85% in first few seconds) followed by logarithmic decline
  
  const baseBreakpoints: Breakpoint[] = [
    {
      id: 'start',
      label: 'Video Start',
      value: 0,
      isPercentile: false,
      viewCount: totalViews
    }
  ]

  // Platform-specific breakpoints with realistic retention
  switch (platform) {
    case 'facebook': {
      // Define ThruPlay breakpoint based on video length
      const isShortVideo = videoLength < 15
      
      // For short videos, ThruPlay is 95% watched
      // For longer videos, ThruPlay is 15 seconds watched
      const thruPlayBreakpoint = {
        id: 'fb-thruplay',
        label: isShortVideo 
          ? 'ThruPlay (95% viewed)' 
          : 'ThruPlay (15 seconds)',
        value: isShortVideo ? 95 : 15,
        isPercentile: isShortVideo,
        // Use constant for initial estimate
        viewCount: Math.round(totalViews * PLATFORM_CONSTANTS.FACEBOOK.THRUPLAY_RETENTION) 
      }
      
      // Calculate ThruPlay as a time point for comparison
      const thruPlayTimeInSeconds = isShortVideo 
        ? (95 / 100) * videoLength  // 95% of video length for short videos
        : 15                        // 15 seconds for longer videos
      
      // Calculate base retention values - adjust for video length logarithmically
      // Shorter videos retain slightly better than longer ones
      const getRetention = (percentage: number): number => {
        // Use constants for base retention estimates
        const lengthFactor = Math.max(0.6, Math.min(1.2, 30 / Math.max(15, videoLength)))
        if (percentage <= 25) {
          return Math.max(5, Math.min(15, (PLATFORM_CONSTANTS.BASE_RETENTION.RETENTION_25 * 1.2) * 100 * lengthFactor)) // ~12% base adjusted
        } else if (percentage <= 50) {
          return Math.max(3, Math.min(10, (PLATFORM_CONSTANTS.BASE_RETENTION.RETENTION_50 * 1.15) * 100 * lengthFactor)) // ~8% base adjusted
        } else if (percentage <= 75) {
          return Math.max(2, Math.min(6, (PLATFORM_CONSTANTS.BASE_RETENTION.RETENTION_75 * 0.8) * 100 * lengthFactor)) // ~4% base adjusted (lower multiplier here)
        } else if (percentage <= 95) {
          return Math.max(1, Math.min(4, (PLATFORM_CONSTANTS.BASE_RETENTION.RETENTION_100 * 0.85) * 100 * lengthFactor)) // ~2.5% base adjusted
        } else {
          return Math.max(1, Math.min(3, (PLATFORM_CONSTANTS.BASE_RETENTION.RETENTION_100 * 0.7) * 100 * lengthFactor)) // ~2% base adjusted
        }
      }
      
      // Generate standard percentage breakpoints
      const standardPercentages = [25, 50, 75, 95, 100]
      const percentageBreakpoints = standardPercentages.map(percentage => {
        // Calculate time in seconds for this percentage
        const timeInSeconds = (percentage / 100) * videoLength
        
        // Skip if EXACTLY the same as ThruPlay (with precision to account for floating point)
        if (Math.abs(timeInSeconds - thruPlayTimeInSeconds) < 0.001) {
          return null
        }
        
        return {
          id: `fb-${percentage}p`,
          label: `${percentage}% Watched`,
          value: percentage,
          isPercentile: true,
          viewCount: Math.round(totalViews * getRetention(percentage) / 100)
        }
      }).filter(Boolean) as Breakpoint[]
      
      return [
        ...baseBreakpoints,
        {
          id: 'fb-3s',
          label: '3-Second Views',
          value: 3,
          isPercentile: false,
          // Use constant for initial drop calculation
          viewCount: Math.round(totalViews * PLATFORM_CONSTANTS.FACEBOOK.INITIAL_DROP_RETENTION) 
        },
        thruPlayBreakpoint,
        ...percentageBreakpoints
      ]
    }

    case 'tiktok': {
      // Calculate base retention values based on video length
      const getRetentionByLength = (percentage: number): number => {
        // Adjust for video length - shorter videos retain better
        const lengthFactor = Math.max(0.6, Math.min(1.2, 15 / Math.max(10, videoLength)))
        
        // Logarithmically decreasing retention curve
        switch (percentage) {
          case 25: return Math.round(totalViews * Math.min(0.15, PLATFORM_CONSTANTS.BASE_RETENTION.RETENTION_25 * lengthFactor)) // 10-15% for 25%
          case 50: return Math.round(totalViews * Math.min(0.10, PLATFORM_CONSTANTS.BASE_RETENTION.RETENTION_50 * lengthFactor)) // 7-10% for 50%
          case 75: return Math.round(totalViews * Math.min(0.07, PLATFORM_CONSTANTS.BASE_RETENTION.RETENTION_75 * lengthFactor)) // 5-7% for 75%
          case 100: return Math.round(totalViews * Math.min(0.05, PLATFORM_CONSTANTS.BASE_RETENTION.RETENTION_100 * lengthFactor)) // 3-5% for 100%
          default: return Math.round(totalViews * 0.05) // Fallback
        }
      }
      
      return [
        ...baseBreakpoints,
        {
          id: 'tt-2s',
          label: '2-Second Views',
          value: 2,
          isPercentile: false,
          // Use constant
          viewCount: Math.round(totalViews * PLATFORM_CONSTANTS.TIKTOK.INITIAL_DROP_RETENTION_2S) 
        },
        {
          id: 'tt-6s',
          label: '6-Second Views',
          value: 6,
          isPercentile: false,
           // Use constant
          viewCount: Math.round(totalViews * PLATFORM_CONSTANTS.TIKTOK.INITIAL_DROP_RETENTION_6S)
        },
        {
          id: 'tt-25p',
          label: '25% Watched',
          value: 25,
          isPercentile: true,
          viewCount: getRetentionByLength(25)
        },
        {
          id: 'tt-50p',
          label: '50% Watched',
          value: 50,
          isPercentile: true,
          viewCount: getRetentionByLength(50)
        },
        {
          id: 'tt-75p',
          label: '75% Watched',
          value: 75,
          isPercentile: true,
          viewCount: getRetentionByLength(75)
        },
        {
          id: 'tt-100p',
          label: '100% Completed',
          value: 100,
          isPercentile: true,
          viewCount: getRetentionByLength(100)
        }
      ]
    }

    case 'youtube': {
      // Calculate base retention values based on video length
      const getRetentionByLength = (percentage: number): number => {
        // Adjust for video length - shorter videos retain better
        const lengthFactor = Math.max(0.6, Math.min(1.2, 60 / Math.max(30, videoLength)))
        
        // YouTube typically has better initial retention but similar completion
        switch (percentage) {
          case 25: return Math.round(totalViews * Math.min(0.20, PLATFORM_CONSTANTS.YOUTUBE.BASE_RETENTION_25 * lengthFactor)) // 15-20% for 25%
          case 50: return Math.round(totalViews * Math.min(0.12, PLATFORM_CONSTANTS.YOUTUBE.BASE_RETENTION_50 * lengthFactor)) // 10-12% for 50%
          case 75: return Math.round(totalViews * Math.min(0.08, PLATFORM_CONSTANTS.YOUTUBE.BASE_RETENTION_75 * lengthFactor)) // 6-8% for 75%
          case 100: return Math.round(totalViews * Math.min(0.06, PLATFORM_CONSTANTS.YOUTUBE.BASE_RETENTION_100 * lengthFactor)) // 4-6% for 100%
          default: return Math.round(totalViews * 0.05) // Fallback
        }
      }
      
      return [
        ...baseBreakpoints,
        {
          id: 'yt-25p',
          label: '25% Watched',
          value: 25,
          isPercentile: true,
          viewCount: getRetentionByLength(25)
        },
        {
          id: 'yt-50p',
          label: '50% Watched',
          value: 50,
          isPercentile: true,
          viewCount: getRetentionByLength(50)
        },
        {
          id: 'yt-75p',
          label: '75% Watched',
          value: 75,
          isPercentile: true,
          viewCount: getRetentionByLength(75)
        },
        {
          id: 'yt-100p',
          label: '100% Completed',
          value: 100,
          isPercentile: true,
          viewCount: getRetentionByLength(100)
        }
      ]
    }

    case 'vimeo': {
      // Vimeo analytics surface percentage-based engagement (plays, and the
      // average-percentage-watched / finishes curve), so model it on the same
      // 25/50/75/100 breakpoints as YouTube — but with Vimeo's much higher
      // embedded/on-site retention (see PLATFORM_CONSTANTS.VIMEO for the
      // Wistia/Vimeo sourcing). The per-quartile ceilings bound the short-video
      // (lengthFactor → 1.2) case at sensible "good embedded video" maxima while
      // preserving a strictly decreasing curve.
      const getRetentionByLength = (percentage: number): number => {
        // Adjust for video length - shorter videos retain better
        const lengthFactor = Math.max(0.6, Math.min(1.2, 60 / Math.max(30, videoLength)))

        switch (percentage) {
          case 25: return Math.round(totalViews * Math.min(0.85, PLATFORM_CONSTANTS.VIMEO.BASE_RETENTION_25 * lengthFactor))
          case 50: return Math.round(totalViews * Math.min(0.68, PLATFORM_CONSTANTS.VIMEO.BASE_RETENTION_50 * lengthFactor))
          case 75: return Math.round(totalViews * Math.min(0.52, PLATFORM_CONSTANTS.VIMEO.BASE_RETENTION_75 * lengthFactor))
          case 100: return Math.round(totalViews * Math.min(0.45, PLATFORM_CONSTANTS.VIMEO.BASE_RETENTION_100 * lengthFactor))
          default: return Math.round(totalViews * 0.35) // Fallback
        }
      }

      return [
        ...baseBreakpoints,
        {
          id: 'vm-25p',
          label: '25% Watched',
          value: 25,
          isPercentile: true,
          viewCount: getRetentionByLength(25)
        },
        {
          id: 'vm-50p',
          label: '50% Watched',
          value: 50,
          isPercentile: true,
          viewCount: getRetentionByLength(50)
        },
        {
          id: 'vm-75p',
          label: '75% Watched',
          value: 75,
          isPercentile: true,
          viewCount: getRetentionByLength(75)
        },
        {
          id: 'vm-100p',
          label: '100% Completed',
          value: 100,
          isPercentile: true,
          viewCount: getRetentionByLength(100)
        }
      ]
    }

    case 'custom':
    default:
      return baseBreakpoints
  }
}
